import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import ReceivePaymentUpdate from '../payments/ReceivePaymentUpdate';
import type { CustomerPayment } from '../../types/customerPayment';
import { useSalesTransactionsHub } from './SalesTransactionsHubContext';

type AllocationsNavState = {
  returnTo?: string;
  returnToPreview?: boolean;
};

const SalesPaymentAllocationsOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useSalesTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const navState = useMemo(
    () => (location.state as AllocationsNavState | null) ?? null,
    [location.state]
  );

  const parsedId = id != null ? Number(id) : Number.NaN;
  const paymentId = Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const close = useCallback(() => {
    if (navState?.returnToPreview && paymentId) {
      navigate(
        {
          pathname: `/sales-transactions/view/payment/${paymentId}`,
          search: listSearch,
        },
        {
          preventScrollReset: true,
          state: navState.returnTo ? { returnTo: navState.returnTo } : null,
        }
      );
      return;
    }

    if (navState?.returnTo) {
      navigate(navState.returnTo, { preventScrollReset: true });
      return;
    }

    navigate({ pathname: '/sales-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navState, navigate, paymentId]);

  const handlePaymentSaved = useCallback(
    (_payment: CustomerPayment) => {
      refetchList();
      close();
    },
    [close, refetchList]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <div className="flex flex-1 min-h-0 flex-col">
        <ReceivePaymentUpdate
          key={paymentId}
          embedded
          paymentId={paymentId}
          onClose={close}
          onPaymentSaved={handlePaymentSaved}
        />
      </div>
    </TransactionOverlay>
  );
};

export default SalesPaymentAllocationsOverlay;
